import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";

const LINKS = [
  { to: "/", key: "nav.home" },
  { to: "/about", key: "nav.about" },
  { to: "/products", key: "nav.products" },
  { to: "/technologies", key: "nav.technologies" },
  { to: "/careers", key: "nav.careers" },
  { to: "/blogs", key: "nav.blogs" },
  { to: "/testimonials", key: "nav.testimonials" },
  { to: "/contact", key: "nav.contact" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  React.useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/75 border-b border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center">
          <Logo size={38} />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-amber-400" : "text-slate-300 hover:text-white"
                }`
              }
              end={l.to === "/"}
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <Link
            to="/demo"
            data-testid="nav-demo-cta"
            className="inline-flex items-center gap-2 bg-amber-500 text-slate-950 font-bold text-sm px-4 py-2 hover:bg-amber-400 transition-colors rounded-sm glow-gold"
          >
            {t("nav.demo")} <ArrowRight size={14} />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-slate-200 p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden border-t border-slate-800 bg-slate-950"
        >
          <div className="px-5 py-4 flex flex-col gap-3">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `py-2 text-base ${isActive ? "text-amber-400" : "text-slate-200"}`
                }
                end={l.to === "/"}
              >
                {t(l.key)}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <LanguageSwitcher />
              <Link
                to="/demo"
                data-testid="mobile-nav-demo-cta"
                className="inline-flex items-center gap-2 bg-amber-500 text-slate-950 font-bold text-sm px-4 py-2 rounded-sm"
              >
                {t("nav.demo")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
